import { useEffect } from 'react';
import type { FieldValues, Path, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { vatFractionFromPercent } from '@/shared/utils/vatRate';
import { deriveFromRate } from '../utils/vat';

interface VatCategoryRate {
    id: string;
    rate_percent: string;
}

interface UseVatDerivedCostArgs<T extends FieldValues> {
    watch: UseFormWatch<T>;
    setValue: UseFormSetValue<T>;
    netField: Path<T>;
    grossField: Path<T>;
    categoryField?: Path<T>;
    categories?: VatCategoryRate[];
    fixedRatePercent?: string;
    enabled?: boolean;
}

/** Derives gross from net at the resolved VAT rate; never reads the gross field. */
export function useVatDerivedCost<T extends FieldValues>({
    watch,
    setValue,
    netField,
    grossField,
    categoryField,
    categories,
    fixedRatePercent,
    enabled = true,
}: UseVatDerivedCostArgs<T>): { rate: string | null } {
    const net = watch(netField);
    const categoryId = categoryField ? watch(categoryField) : undefined;
    const ratePercent = categoryField
        ? categories?.find((c) => c.id === categoryId)?.rate_percent
        : fixedRatePercent;
    const rate = vatFractionFromPercent(ratePercent ?? '0');

    useEffect(() => {
        if (!enabled) return;
        const value = rate === null || net === undefined || net === null || net === ''
            ? null
            : deriveFromRate(String(net), rate, 'grossFromNet');
        setValue(grossField, value as never);
    }, [net, rate, enabled, grossField, setValue]);

    return { rate };
}
